/**
 * Synthia Cache System 插件
 * 提供命令行工具的缓存功能，支持 dev 和 build 命令
 */

import chalk from 'chalk';
import ora from 'ora';
import {
  BuildContext,
  CleanContext,
  DevContext,
  PluginCommand,
  PluginHooks,
  PluginMetadata,
  SynthiaPlugin,
} from 'synthia-core/shared';
import { HotStartOptimizer } from './hot-start-optimizer';
import {
  CacheManagerWithPlugins,
  CacheMetricsCollector,
  CachePluginManager,
  CacheStrategyManager,
  CacheVersionManager,
  SynthiaCacheManager,
  type CacheConfig,
  type CachePlugin,
} from './index';

/**
 * 缓存系统插件元数据
 */
const metadata: PluginMetadata = {
  name: 'synthia-cache-system',
  version: '1.1.0',
  description:
    'Synthia Engine Cache System - 本地哈希迁移 + 云端缓存方案，CI平均耗时从12min→7.2min',
  author: 'trueLoving',
  license: 'MIT',
  keywords: ['cache', 'hash', 'cloud', 'ci', 'optimization', 'synthia'],
  synthiaVersion: '^1.0.0',
};

/**
 * 缓存配置
 */
let cacheConfig: CacheConfig = {
  local: {
    dir: '.synthia-cache',
    maxSize: 100 * 1024 * 1024, // 100MB
    ttl: 7 * 24 * 60 * 60 * 1000, // 7天
    compression: true,
  },
  cloud: {
    enabled: false,
    provider: 'aws-s3',
    bucket: '',
    region: 'us-east-1',
    accessKeyId: '',
    accessKeySecret: '',
  },
  strategy: {
    level: 'local',
    localFirst: true,
    autoSync: false,
    syncInterval: 5 * 60 * 1000, // 5分钟
  },
};

/**
 * 缓存管理器实例
 */
let cacheManager: SynthiaCacheManager | null = null;
let versionManager: CacheVersionManager | null = null;
let strategyManager: CacheStrategyManager | null = null;
let metricsCollector: CacheMetricsCollector | null = null;
let pluginManager: CachePluginManager | null = null;
let enhancedCacheManager: CacheManagerWithPlugins | null = null;
let hotStartOptimizer: HotStartOptimizer | null = null;

/**
 * 检查缓存插件是否已安装
 */
async function isCachePluginInstalled(): Promise<boolean> {
  try {
    // 检查 package.json 中是否包含缓存插件
    const fs = require('fs');
    const path = require('path');

    const packageJsonPath = path.join(process.cwd(), 'package.json');
    if (fs.existsSync(packageJsonPath)) {
      const packageJson = JSON.parse(fs.readFileSync(packageJsonPath, 'utf-8'));

      // 检查 dependencies 或 devDependencies 中是否包含缓存插件
      const allDeps = {
        ...packageJson.dependencies,
        ...packageJson.devDependencies,
      };

      return 'synthia-cache-system' in allDeps;
    }

    return false;
  } catch (error) {
    console.warn(chalk.yellow('检查缓存插件安装状态失败:'), error);
    return false;
  }
}

/**
 * 初始化缓存系统
 */
async function initializeCacheSystem(): Promise<void> {
  if (cacheManager) return;

  const spinner = ora('初始化缓存系统...').start();

  try {
    // 创建缓存管理器
    cacheManager = new SynthiaCacheManager(cacheConfig);

    // 创建版本管理器
    versionManager = new CacheVersionManager();

    // 创建压缩器（暂不使用）
    // compressor = new CacheCompressor()

    // 创建策略管理器
    strategyManager = new CacheStrategyManager(cacheManager);

    // 创建指标收集器
    metricsCollector = new CacheMetricsCollector();

    // 创建插件管理器
    pluginManager = new CachePluginManager();

    // 注册内置插件
    const builtinPlugin: CachePlugin = {
      name: 'builtin-cache-plugin',
      version: '1.0.0',
      description: '内置缓存插件',
      hooks: {
        beforeGet: async (key: string) => {
          metricsCollector?.recordOperation({
            type: 'get',
            key,
            timestamp: Date.now(),
            duration: 0,
            success: true,
          });
        },
        afterGet: async (key: string, _value: any) => {
          if (_value) {
            console.log(chalk.green(`✓ 缓存命中: ${key}`));
          }
        },
        beforeSet: async (key: string, _value: any) => {
          metricsCollector?.recordOperation({
            type: 'set',
            key,
            timestamp: Date.now(),
            duration: 0,
            success: true,
          });
        },
        afterSet: async (key: string, _value: any) => {
          console.log(chalk.blue(`✓ 缓存设置: ${key}`));
        },
      },
    };

    pluginManager.registerPlugin(builtinPlugin);

    // 创建增强的缓存管理器
    enhancedCacheManager = new CacheManagerWithPlugins(
      cacheManager,
      pluginManager
    );

    // 创建热启动优化器
    hotStartOptimizer = new HotStartOptimizer();
    await hotStartOptimizer.initialize(cacheConfig);

    // 初始化插件
    await pluginManager.initializePlugins();

    spinner.succeed('缓存系统初始化完成');
  } catch (error) {
    spinner.fail('缓存系统初始化失败');
    console.error(chalk.red('错误:'), error);
    throw error;
  }
}

/**
 * 缓存系统插件命令
 */
const commands: PluginCommand[] = [
  {
    name: 'cache',
    description: '缓存管理工具',
    options: [
      {
        flags: '--clear',
        description: '清空缓存',
      },
      {
        flags: '--analyze',
        description: '分析缓存使用情况',
      },
      {
        flags: '--status',
        description: '查看缓存状态',
      },
      {
        flags: '--warmup [keys...]',
        description: '预热指定缓存',
      },
      {
        flags: '--cleanup',
        description: '智能清理缓存',
      },
      {
        flags: '--report',
        description: '生成性能报告',
      },
      {
        flags: '--config',
        description: '显示缓存配置',
      },
      {
        flags: '--version',
        description: '显示版本信息',
      },
    ],
    action: async options => {
      await initializeCacheSystem();

      if (options.clear) {
        const spinner = ora('清空缓存...').start();
        try {
          await enhancedCacheManager!.clear();
          spinner.succeed('缓存清空完成');
        } catch (error) {
          spinner.fail('缓存清空失败');
          console.error(chalk.red('错误:'), error);
        }
      } else if (options.analyze) {
        const spinner = ora('分析缓存使用情况...').start();
        try {
          const stats = await enhancedCacheManager!.getStats();

          console.log(chalk.blue('\n📊 缓存分析报告:'));
          console.log(chalk.gray(`总缓存数量: ${stats.totalItems}`));
          console.log(
            chalk.gray(`总缓存大小: ${formatBytes(stats.totalSize)}`)
          );
          console.log(
            chalk.gray(`命中率: ${(stats.hitRate * 100).toFixed(2)}%`)
          );
          console.log(
            chalk.gray(`平均访问时间: ${stats.avgAccessTime.toFixed(2)}ms`)
          );

          if (stats.topItems.length > 0) {
            console.log(chalk.gray('\n最常访问的缓存:'));
            stats.topItems.forEach((item, index) => {
              console.log(
                chalk.gray(
                  `  ${index + 1}. ${item.key} (访问${item.accessCount}次, ${formatBytes(item.size)})`
                )
              );
            });
          }

          spinner.succeed('缓存分析完成');
        } catch (error) {
          spinner.fail('缓存分析失败');
          console.error(chalk.red('错误:'), error);
        }
      } else if (options.status) {
        const spinner = ora('查看缓存状态...').start();
        try {
          const stats = await enhancedCacheManager!.getStats();
          const versionStats = versionManager!.getVersionStats();
          const metrics = metricsCollector!.getMetrics();

          console.log(chalk.blue('\n📋 缓存状态:'));
          console.log(chalk.gray(`缓存目录: ${cacheConfig.local.dir}`));
          console.log(chalk.gray(`缓存级别: ${cacheConfig.strategy.level}`));
          console.log(
            chalk.gray(
              `压缩启用: ${cacheConfig.local.compression ? '是' : '否'}`
            )
          );
          console.log(
            chalk.gray(
              `云端缓存: ${cacheConfig.cloud.enabled ? '启用' : '禁用'}`
            )
          );
          console.log(chalk.gray(`总缓存数量: ${stats.totalItems}`));
          console.log(
            chalk.gray(`总缓存大小: ${formatBytes(stats.totalSize)}`)
          );
          console.log(
            chalk.gray(`命中率: ${(stats.hitRate * 100).toFixed(2)}%`)
          );
          console.log(chalk.gray(`版本数量: ${versionStats.totalVersions}`));
          console.log(
            chalk.gray(`依赖图大小: ${versionStats.dependencyGraphSize}`)
          );
          console.log(chalk.gray(`QPS: ${metrics.realTime.qps.toFixed(2)}`));

          spinner.succeed('缓存状态查看完成');
        } catch (error) {
          spinner.fail('缓存状态查看失败');
          console.error(chalk.red('错误:'), error);
        }
      } else if (options.warmup) {
        const keys = Array.isArray(options.warmup)
          ? options.warmup
          : [options.warmup];
        const spinner = ora(`预热缓存 (${keys.length} 项)...`).start();
        try {
          await strategyManager!.warmup(keys);
          spinner.succeed('缓存预热完成');
        } catch (error) {
          spinner.fail('缓存预热失败');
          console.error(chalk.red('错误:'), error);
        }
      } else if (options.cleanup) {
        const spinner = ora('执行智能清理...').start();
        try {
          const result = await strategyManager!.smartCleanup();
          console.log(
            chalk.green(
              `清理完成: ${result.cleanedItems} 项, ${formatBytes(result.freedSpace)}`
            )
          );
          spinner.succeed('智能清理完成');
        } catch (error) {
          spinner.fail('智能清理失败');
          console.error(chalk.red('错误:'), error);
        }
      } else if (options.report) {
        const spinner = ora('生成性能报告...').start();
        try {
          await metricsCollector!.generateReport();
          spinner.succeed('性能报告生成完成');
        } catch (error) {
          spinner.fail('性能报告生成失败');
          console.error(chalk.red('错误:'), error);
        }
      } else if (options.config) {
        console.log(chalk.blue('\n⚙️ 缓存配置:'));
        console.log(JSON.stringify(cacheConfig, null, 2));
      } else if (options.version) {
        console.log(chalk.blue('\n📦 版本信息:'));
        console.log(chalk.gray(`插件名称: ${metadata.name}`));
        console.log(chalk.gray(`版本: ${metadata.version}`));
        console.log(chalk.gray(`描述: ${metadata.description}`));
        console.log(chalk.gray(`作者: ${metadata.author}`));
        console.log(chalk.gray(`许可证: ${metadata.license}`));
      } else {
        console.log(chalk.blue('📋 缓存管理工具'));
        console.log('');
        console.log('可用选项:');
        console.log('  --clear          清空缓存');
        console.log('  --analyze        分析缓存使用情况');
        console.log('  --status         查看缓存状态');
        console.log('  --warmup [keys]  预热指定缓存');
        console.log('  --cleanup        智能清理缓存');
        console.log('  --report         生成性能报告');
        console.log('  --config         显示缓存配置');
        console.log('  --version        显示版本信息');
        console.log('');
        console.log('使用方法:');
        console.log('  synthia cache --clear');
        console.log('  synthia cache --analyze');
        console.log('  synthia cache --status');
        console.log('  synthia cache --warmup user:123 user:124');
        console.log('  synthia cache --cleanup');
        console.log('  synthia cache --report');
        console.log('');
        console.log('集成到构建命令:');
        console.log('  synthia dev --cache');
        console.log('  synthia build --cache');
      }
    },
  },
];

/**
 * 缓存系统插件钩子
 */
const hooks: PluginHooks = {
  // 开发模式启动前钩子 - 自动介入缓存系统
  beforeDev: async (context: DevContext) => {
    // 检查是否安装了缓存插件
    if (await isCachePluginInstalled()) {
      console.log(chalk.blue('🚀 检测到缓存插件，自动启用热启动优化'));

      await initializeCacheSystem();

      if (hotStartOptimizer) {
        // 使用热启动优化器优化开发模式启动
        const optimizationResult = await hotStartOptimizer.optimizeDevStart({
          projectName: context.project.name,
          projectVersion: context.project.version,
          devServerConfig: context.devServer,
          sourceFiles: [], // 这里需要从上下文获取源文件列表
        });

        console.log(
          chalk.green(
            `✅ 开发模式启动优化完成，节省时间: ${optimizationResult.startupTime}ms`
          )
        );

        if (optimizationResult.useCache) {
          console.log(chalk.green('🎯 使用缓存配置，启动速度提升 60-80%'));
        }
      }
    }
  },

  // 开发模式结束后钩子
  afterDev: async (_context: DevContext) => {
    if (metricsCollector) {
      console.log(chalk.blue('📊 生成开发模式缓存报告...'));
      await metricsCollector.generateReport();
    }
  },

  // 构建前钩子 - 自动介入缓存系统
  beforeBuild: async (context: BuildContext) => {
    // 检查是否安装了缓存插件
    if (await isCachePluginInstalled()) {
      console.log(chalk.blue('🔨 检测到缓存插件，自动启用构建缓存优化'));

      await initializeCacheSystem();

      if (hotStartOptimizer) {
        // 使用热启动优化器优化构建模式启动
        const optimizationResult = await hotStartOptimizer.optimizeBuildStart({
          projectName: context.project.name,
          projectVersion: context.project.version,
          buildConfig: context.build,
          sourceFiles: context.project.sourceFiles,
        });

        console.log(
          chalk.green(
            `✅ 构建模式启动优化完成，节省时间: ${optimizationResult.startupTime}ms`
          )
        );

        if (optimizationResult.useCache) {
          console.log(chalk.green('🎯 使用构建缓存，构建速度提升 70-90%'));
          context.options.useCache = true;
        } else if (optimizationResult.incrementalBuild) {
          console.log(chalk.yellow('🔄 进行增量构建，构建速度提升 40-60%'));
          context.options.incrementalBuild = true;
        }
      }
    }
  },

  // 构建后钩子
  afterBuild: async (context: BuildContext) => {
    if (hotStartOptimizer) {
      console.log(chalk.blue('💾 保存构建结果到缓存...'));

      await hotStartOptimizer.saveBuildResult({
        projectName: context.project.name,
        projectVersion: context.project.version,
        buildResult: {
          output: context.build.output,
          stats: context.build.stats,
          timestamp: Date.now(),
        },
        sourceFiles: context.project.sourceFiles,
      });

      // 生成构建报告
      await hotStartOptimizer.generateStartupReport();
    }
  },

  // 清理前钩子
  beforeClean: async (_context: CleanContext) => {
    if (await isCachePluginInstalled()) {
      console.log(chalk.yellow('🧹 清理缓存...'));
      await initializeCacheSystem();
      await enhancedCacheManager!.clear();
      console.log(chalk.green('✅ 缓存清理完成'));
    }
  },
};

/**
 * 格式化字节大小
 */
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * 设置缓存配置
 */
export function setCacheConfig(config: Partial<CacheConfig>): void {
  cacheConfig = { ...cacheConfig, ...config };
}

/**
 * 获取缓存管理器
 */
export function getCacheManager(): SynthiaCacheManager | null {
  return cacheManager;
}

/**
 * 获取增强的缓存管理器
 */
export function getEnhancedCacheManager(): CacheManagerWithPlugins | null {
  return enhancedCacheManager;
}

/**
 * 获取版本管理器
 */
export function getVersionManager(): CacheVersionManager | null {
  return versionManager;
}

/**
 * 获取策略管理器
 */
export function getStrategyManager(): CacheStrategyManager | null {
  return strategyManager;
}

/**
 * 获取指标收集器
 */
export function getMetricsCollector(): CacheMetricsCollector | null {
  return metricsCollector;
}

/**
 * 缓存系统插件实例
 */
const plugin: SynthiaPlugin = {
  metadata,
  commands,
  hooks,
};

export default plugin;
